import {observer} from 'mobx-react-lite';
import Script from 'next/script';

/**
 * A component responsible for injecting TagCommander tracking scripts into the application.
 * The `Tracking` component uses Next.js `Script` to configure and load TagCommander for analytics purposes.
 * It defines global variables (`tc_vars`) that capture page metadata, environment context (LIVE or STAGE),
 * and navigation details like referrer and destination URLs. These variables are used by TagCommander for
 * categorizing and analyzing page views.
 * The configuration script is loaded with the `beforeInteractive` strategy to ensure tracking is initialized
 * early in the lifecycle. The external TagCommander script is then loaded with `afterInteractive` to
 * initialize tracking logic after the page becomes interactive.
 *
 * @returns A fragment containing inline configuration and an external TagCommander script for analytics tracking.
 *
 * @example
 * ```tsx
 * // Add tracking to your main app layout
 * function AppLayout({ children }: { children: React.ReactNode }) {
 *   return (
 *     <>
 *       <Tracking />
 *       {children}
 *     </>
 *   );
 * }
 * ```
 */
const Tracking = observer(() => (
    <>
        <Script id="tagCommanderConfig" strategy="beforeInteractive">
            {`
            window.tc_vars = {
                staged: "${process.env.FEATURE_ENV === 'live' ? 'LIVE' : 'STAGE'}",
                version: "1.0",
                page_pageName: "INDEX_Startseite|123",
                page_referringURL: document.referrer,
                page_destinationURL: window.location.href,
                page_pageID: "ISARLICHT-SITE|INDEX_Startseite|123",
                page_primaryCategory: "ISARLICHT-SITE",
                page_subCategory1: "Startseite",
                page_pageInstanceID: "LIVE|ISARLICHT-SITE|INDEX_Startseite|123",
                page_version: "1.0",
                product_primaryCategory: "ISARLICHT-SITE",
                page_pageType: "Landing-Pages",
            };
            `}
        </Script>
        <Script src="https://cdn.tagcommander.com/7585/tc_Isarlicht_20.js" strategy="afterInteractive" />
    </>
));

Tracking.displayName = 'Tracking';

export {Tracking};